import { Dexie } from 'dexie';
import { decorateStorage, StorageKey, TypeRegistry, Metadata } from '@polkadot/types';
import { hasOwnProperty, BigMath, isArrayOf, planckToTokens } from '@talismn/util';
import groupBy from 'lodash/groupBy';
import anylogger from 'anylogger';

// TODO: Document default balances module purpose/usage
const DefaultBalanceModule = type => ({
  get type() {
    return type;
  },
  async fetchSubstrateChainMeta() {
    return null;
  },
  async fetchEvmChainMeta() {
    return null;
  },
  async fetchSubstrateChainTokens() {
    return Promise.resolve({});
  },
  async fetchEvmChainTokens() {
    return Promise.resolve({});
  },
  async subscribeBalances(_, callback) {
    callback(new Error("Balance subscriptions are not implemented in this module."));
    return () => {};
  },
  async fetchBalances() {
    throw new Error("Balance fetching is not implemented in this module.");
  },
  async transferToken() {
    throw new Error("Token transfers are not implemented in this module.");
  }
});

//
// internal
//

class TalismanBalancesDatabase extends Dexie {
  constructor() {
    super("TalismanBalances");

    // https://dexie.org/docs/Tutorial/Design#database-versioning
    this.version(1).stores({
      // You only need to specify properties that you wish to index.
      // The object store will allow any properties on your stored objects but you can only query them by indexed properties
      // https://dexie.org/docs/API-Reference#declare-database
      //
      // Never index properties containing images, movies or large (huge) strings. Store them in IndexedDB, yes! but just don’t index them!
      // https://dexie.org/docs/Version/Version.stores()#warning
      balances: "id, source, status, address, tokenId"
    });

    // this.on("ready", async () => {})
  }
}

const db = new TalismanBalancesDatabase();

var packageJson = {
	name: "@talismn/balances",
	version: "0.0.0-pr971-20230727205848",
	author: "Talisman",
	homepage: "https://talisman.xyz",
	license: "GPL-3.0-or-later",
	publishConfig: {
		access: "public"
	},
	repository: {
		directory: "packages/balances",
		type: "git",
		url: "https://github.com/talismansociety/talisman.git"
	},
	main: "dist/talismn-balances.cjs.js",
	module: "dist/talismn-balances.esm.js",
	files: [
		"/dist",
		"/plugins"
	],
	engines: {
		node: ">=18"
	},
	scripts: {
		test: "jest",
		lint: "eslint src --max-warnings 0",
		clean: "rm -rf dist && rm -rf .turbo rm -rf node_modules"
	},
	dependencies: {
		"@talismn/chain-connector": "workspace:*",
		"@talismn/chain-connector-evm": "workspace:*",
		"@talismn/chaindata-provider": "workspace:*",
		"@talismn/token-rates": "workspace:*",
		"@talismn/util": "workspace:*",
		anylogger: "^1.0.11",
		dexie: "^3.2.4",
		lodash: "4.17.21"
	},
	devDependencies: {
		"@polkadot/types": "^10.1.4",
		"@talismn/eslint-config": "workspace:*",
		"@talismn/tsconfig": "workspace:*",
		"@types/jest": "^27.5.1",
		"@types/lodash": "^4.14.180",
		eslint: "^8.4.0",
		jest: "^28.1.0",
		"ts-jest": "^28.0.2",
		typescript: "^4.6.4"
	},
	peerDependencies: {
		"@polkadot/types": "10.x"
	},
	preconstruct: {
		entrypoints: [
			"index.ts",
			"plugins.ts"
		]
	},
	eslintConfig: {
		root: true,
		"extends": [
			"@talismn/eslint-config/base"
		]
	}
};

var log = anylogger(packageJson.name);

/**
 * Wraps a BalanceModule's fetch/subscribe methods with a single `balances` method.
 * This `balances` method will subscribe if a callback parameter is provided, or otherwise fetch.
 */

async function balances(balanceModule, addressesByToken, callback) {
  // subscription request
  if (callback !== undefined) return await balanceModule.subscribeBalances(addressesByToken, callback);

  // one-off request
  return await balanceModule.fetchBalances(addressesByToken);
}
const createTypeRegistryCache = () => {
  const typeRegistryCache = new Map();
  const getOrCreateTypeRegistry = (chainId, metadataRpc) => {
    const cached = typeRegistryCache.get(chainId);
    if (cached && cached.metadataRpc === metadataRpc) return cached.typeRegistry;
    const typeRegistry = new TypeRegistry();
    if (typeof metadataRpc === "string") {
      try {
        const metadata = new Metadata(typeRegistry, metadataRpc);
        metadata.registry.setMetadata(metadata);
      } catch (cause) {
        log.warn(new Error(`Failed to set metadata for chain ${chainId}`, {
          cause
        }), cause);
      }
    }
    typeRegistryCache.set(chainId, {
      metadataRpc,
      typeRegistry
    });
    return typeRegistry;
  };
  return {
    getOrCreateTypeRegistry
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any

// eslint-disable-next-line @typescript-eslint/no-explicit-any

/**
 * The following `Infer*` collection of generic types can be used when you want to
 * extract one of the generic type arguments from an existing BalanceModule.
 *
 * For example, you might want to write a function which can accept any BalanceModule
 * as an input, and then return the specific TokenType for that module:
 * function getTokens<T extends AnyBalanceModule>(module: T): InferTokenType<T>
 *
 * Or for another example, you might want a function which can take any BalanceModule `type`
 * string as input, and then return some data associated with that module with the correct type:
 * function getChainMeta<T extends AnyBalanceModule>(type: InferModuleType<T>): InferChainMeta<T> | undefined
 */

/**
 * Given a `moduleType` and a `chain` from a chaindataProvider, this function will find the chainMeta
 * associated with the given balanceModule for the given chain.
 */
const findChainMeta = (moduleType, chain) => {
  return (chain?.balanceMetadata ?? []).find(meta => meta.moduleType === moduleType)?.metadata;
};
const filterMirrorTokens = (balance, i, balances) => {
  const mirrorOf = balance.token?.mirrorOf;
  return !mirrorOf || !balances.find(b => b.tokenId === mirrorOf);
};
const getValidSubscriptionIds = () => {
  return new Set(localStorage.getItem("TalismanBalancesSubscriptionIds")?.split(",") ?? []);
};
const createSubscriptionId = () => {
  // delete current id (if exists)
  deleteSubscriptionId();

  // create new id
  const subscriptionId = Date.now().toString();
  sessionStorage.setItem("TalismanBalancesSubscriptionId", subscriptionId);

  // add to list of current ids
  const subscriptionIds = getValidSubscriptionIds();
  subscriptionIds.add(subscriptionId);
  localStorage.setItem("TalismanBalancesSubscriptionIds", [...subscriptionIds].filter(Boolean).join(","));
  return subscriptionId;
};
const deleteSubscriptionId = () => {
  const subscriptionId = sessionStorage.getItem("TalismanBalancesSubscriptionId");
  if (!subscriptionId) return;
  const subscriptionIds = getValidSubscriptionIds();
  subscriptionIds.delete(subscriptionId);
  localStorage.setItem("TalismanBalancesSubscriptionIds", [...subscriptionIds].filter(Boolean).join(","));
};

/**
 * Sets all balance statuses from `live-${string}` to either `live` or `cached`
 */
const deriveStatuses = (validSubscriptionIds, balances) => balances.map(balance => {
  if (balance.status === "live" || balance.status === "cache" || balance.status === "stale") return balance;
  if (validSubscriptionIds.length < 1) return {
    ...balance,
    status: "cache"
  };
  if (!validSubscriptionIds.includes(balance.status.slice("live-".length))) return {
    ...balance,
    status: "cache"
  };
  return {
    ...balance,
    status: "live"
  };
});

/**
 * Used by a variety of balance modules to help encode and decode substrate state calls.
 */
class StorageHelper {
  #registry;
  #storageKey;
  #module;
  #method;
  #parameters;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tags = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(registry, module, method, ...parameters) {
    this.#registry = registry;
    this.#module = module;
    this.#method = method;
    this.#parameters = parameters;
    const _metadataVersion = 0; // is not used inside the decorateStorage function
    let query;
    try {
      query = decorateStorage(registry, registry.metadata, _metadataVersion);
    } catch (error) {
      log.debug(`Failed to decorate storage: ${error.message}`);
      this.#storageKey = null;
    }
    try {
      if (!query) throw new Error(`decoratedStorage unavailable`);
      this.#storageKey = new StorageKey(registry, parameters ? [query[module][method], parameters] : query[module][method]);
    } catch (error) {
      log.debug(`Failed to create storageKey ${module || "unknown"}.${method || "unknown"}: ${error.message}`);
      this.#storageKey = null;
    }
  }
  get stateKey() {
    return this.#storageKey?.toHex();
  }
  get module() {
    return this.#module;
  }
  get method() {
    return this.#method;
  }
  get parameters() {
    return this.#parameters;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tag(tags) {
    this.tags = tags;
    return this;
  }
  decode(input) {
    if (!this.#storageKey) return;
    return this.#decodeStorageScaleResponse(this.#registry, this.#storageKey, input);
  }
  #decodeStorageScaleResponse(typeRegistry, storageKey, input) {
    if (input === undefined || input === null) return;
    const type = storageKey.outputType || "Raw";
    const meta = storageKey.meta || {
      fallback: undefined,
      modifier: {
        isOptional: true
      },
      type: {
        asMap: {
          linked: {
            isTrue: false
          }
        },
        isMap: false
      }
    };
    try {
      return typeRegistry.createTypeUnsafe(type, [meta.modifier.isOptional ? typeRegistry.createTypeUnsafe(type, [input], {
        isPedantic: true
      }) : input], {
        isOptional: meta.modifier.isOptional,
        isPedantic: !meta.modifier.isOptional
      });
    } catch (error) {
      throw new Error(`Unable to decode storage ${storageKey.section || "unknown"}.${storageKey.method || "unknown"}: ${error.message}`);
    }
  }
}

/**
 * Pass some these into an `RpcStateQueryHelper` in order to easily batch multiple state queries into the one rpc call.
 */

/**
 * Used by a variety of balance modules to help batch multiple state queries into the one rpc call.
 */
class RpcStateQueryHelper {
  #chainConnector;
  #queries;
  constructor(chainConnector, queries) {
    this.#chainConnector = chainConnector;
    this.#queries = queries;
  }
  async subscribe(callback, timeout = false, subscribeMethod = "state_subscribeStorage", responseMethod = "state_storage", unsubscribeMethod = "state_unsubscribeStorage") {
    const queriesByChain = groupBy(this.#queries, "chainId");
    const subscriptions = Object.entries(queriesByChain).map(([chainId, queries]) => {
      const params = [queries.map(({
        stateKey
      }) => stateKey)];
      const unsub = this.#chainConnector.subscribe(chainId, subscribeMethod, responseMethod, params, (error, result) => {
        error ? callback(error) : callback(null, this.#distributeChangesToQueryDecoders.call(this, chainId, result));
      }, timeout);
      return () => unsub.then(unsubscribe => unsubscribe(unsubscribeMethod));
    });
    return () => subscriptions.forEach(unsubscribe => unsubscribe());
  }
  async fetch(method = "state_queryStorageAt") {
    const queriesByChain = groupBy(this.#queries, "chainId");
    const resultsByChain = await Promise.all(Object.entries(queriesByChain).map(async ([chainId, queries]) => {
      const params = [queries.map(({
        stateKey
      }) => stateKey)];
      const result = (await this.#chainConnector.send(chainId, method, params))[0];
      return this.#distributeChangesToQueryDecoders.call(this, chainId, result);
    }));
    return resultsByChain.flatMap(result => result);
  }
  #distributeChangesToQueryDecoders(chainId, result) {
    if (typeof result !== "object" || result === null) return [];
    if (!hasOwnProperty(result, "changes") || typeof result.changes !== "object") return [];
    if (!Array.isArray(result.changes)) return [];
    return result.changes.flatMap(([reference, change]) => {
      if (typeof reference !== "string") {
        log.warn(`Received non-string reference in RPC result: ${reference}`);
        return [];
      }
      if (typeof change !== "string" && change !== null) {
        log.warn(`Received non-string and non-null change in RPC result: ${reference} | ${change}`);
        return [];
      }
      const query = this.#queries.find(({
        chainId: cId,
        stateKey
      }) => cId === chainId && stateKey === reference);
      if (!query) {
        log.warn(`Failed to find query:\n${reference} in\n${this.#queries.map(({
          stateKey
        }) => stateKey)}`);
        return [];
      }
      return [query.decodeResult(change)];
    });
  }
}

/**
 * `BalanceTypes` is an automatically determined sub-selection of `PluginBalanceTypes`.
 *
 * It is the same list, but with any invalid `BalanceType` definitions filtered out.
 */

/**
 * The `BalanceJson` sum type, which is a union of all of the possible `BalanceTypes`.
 *
 * Each variant comes from a plugin in use by the consuming app.
 *
 * For example, in an app with the `substrate-native`, `evm-native`, `substrate-orml` and `evm-erc20` plugins:
 *
 *     type BalanceJson = SubNativeBalance | EvmNativeBalance | SubOrmlBalance | EvmErc20Balance
 *
 * If `BalanceTypes` is empty then `BalanceJson` will fall back to the common `IBalance` interface, which every balance must implement.
 */

/** A collection of `BalanceJson` objects */

const BalanceStatusLive = subscriptionId => `live-${subscriptionId}`;

/** `IBalance` is a common interface which all balance types must implement. */

/** An unlabelled amount of a balance */

/** A labelled amount of a balance */

/** A labelled locked amount of a balance */

function excludeFromTransferableAmount(locks) {
  if (typeof locks === "string") return BigInt(locks);
  if (!Array.isArray(locks)) locks = [locks];
  return locks.filter(lock => lock.includeInTransferable !== true).map(lock => BigInt(lock.amount)).reduce((max, lock) => BigMath.max(max, lock), 0n);
}
function excludeFromFeePayableLocks(locks) {
  if (typeof locks === "string") return [];
  if (!Array.isArray(locks)) locks = [locks];
  return locks.filter(lock => lock.excludeFromFeePayable);
}

/** A labelled extra amount of a balance */

function includeInTotalExtraAmount(extra) {
  if (!extra) return 0n;
  if (!Array.isArray(extra)) extra = [extra];
  return extra.filter(extra => extra.includeInTotal).map(extra => BigInt(extra.amount)).reduce((a, b) => a + b, 0n);
}

/** Used by plugins to help define their custom `BalanceType` */

/**
 * Have the importing library define its Token and BalanceJson enums (as a sum type of all plugins) and pass them into some
 * internal global typescript context, which is then picked up on by this module.
 */

/** A utility type used to extract the underlying `BalanceType` of a specific source from a generalised `BalanceJson` */
/** TODO: Remove this in favour of a frontend-friendly `ChaindataProvider` */
/**
 * A collection of balances.
 */
class Balances {
  //
  // Properties
  //

  #balances = [];

  //
  // Methods
  //

  constructor(balances, hydrate) {
    // handle Balances (convert to Balance[])
    if (balances instanceof Balances) return new Balances(balances.each, hydrate);

    // handle Balance (convert to Balance[])
    if (balances instanceof Balance) return new Balances([balances], hydrate);

    // handle BalanceJsonList (the only remaining non-array type of balances) (convert to BalanceJson[])
    if (!Array.isArray(balances)) return new Balances(Object.values(balances), hydrate);

    // handle no balances
    if (balances.length === 0) return this;

    // handle BalanceJson[]
    if (!isArrayOf(balances, Balance)) return new Balances(balances.map(storage => new Balance(storage)), hydrate);

    // handle Balance[]
    this.#balances = balances;
    if (hydrate !== undefined) this.hydrate(hydrate);
  }

  /**
   * Calling toJSON on a collection of balances will return the underlying BalanceJsonList.
   */
  toJSON = () => Object.fromEntries(this.#balances.map(balance => {
    try {
      return [balance.id, balance.toJSON()];
    } catch (error) {
      log.error("Failed to convert balance to JSON", error, {
        id: balance.id,
        balance
      });
      return null;
    }
  }).filter(Array.isArray));

  /**
   * Allows the collection to be iterated over.
   *
   * @example
   * [...balances].forEach(balance => { // do something // })
   *
   * @example
   * for (const balance of balances) {
   *   // do something
   * }
   */
  [Symbol.iterator] = () =>
  // Create an array of the balances in this collection and return the result of its iterator.
  this.#balances[Symbol.iterator]();

  /**
   * Hydrates all balances in this collection.
   *
   * @param sources - The sources to hydrate from.
   */
  hydrate = sources => {
    this.#balances.map(balance => balance.hydrate(sources));
  };

  /**
   * Retrieve a balance from this collection by id.
   *
   * @param id - The id of the balance to fetch.
   * @returns The balance if one exists, or none.
   */
  get = id => this.#balances.find(balance => balance.id === id) ?? null;

  /**
   * Retrieve balances from this collection by search query.
   *
   * @param query - The search query.
   * @returns All balances which match the query.
   */
  find = query => {
    // construct filter
    const orQueries = (Array.isArray(query) ? query : [query]).map(query => typeof query === "function" ? query : Object.entries(query));

    // filter balances
    const filter = balance => orQueries.some(query => typeof query === "function" ? query(balance) : query.every(([key, value]) => balance[key] === value));

    // return filter matches
    return new Balances([...this].filter(filter));
  };

  /**
   * Filters this collection to exclude token balances where the token has a `mirrorOf` field
   * and another balance exists in this collection for the token specified by the `mirrorOf` field.
   */
  filterMirrorTokens = () => new Balances([...this].filter(filterMirrorTokens));

  /**
   * Filters this collection to only include balances which are not zero.
   */
  filterNonZero = type => {
    const filter = balance => balance[type].planck > 0n;
    return this.find(filter);
  };
  /**
   * Filters this collection to only include balances which are not zero AND have a fiat conversion rate.
   */
  filterNonZeroFiat = (type, currency) => {
    const filter = balance => (balance[type].fiat(currency) ?? 0) > 0;
    return this.find(filter);
  };

  /**
   * Add some balances to this collection.
   * Added balances take priority over existing balances.
   * The aggregation of the two collections is returned.
   * The original collection is not mutated.
   *
   * @param balances - Either a balance or collection of balances to add.
   * @returns The new collection of balances.
   */
  add = balances => {
    // handle single balance
    if (balances instanceof Balance) return this.add(new Balances(balances));

    // merge balances
    const mergedBalances = Object.fromEntries(this.#balances.map(balance => [balance.id, balance]));
    balances.each.forEach(balance => mergedBalances[balance.id] = balance);

    // return new balances
    return new Balances(Object.values(mergedBalances));
  };

  /**
   * Remove balances from this collection by id.
   * A new collection without these balances is returned.
   * The original collection is not mutated.
   *
   * @param ids - The id(s) of the balances to remove.
   * @returns The new collection of balances.
   */
  remove = ids => {
    // handle single id
    if (!Array.isArray(ids)) return this.remove([ids]);

    // merge and return new balances
    return new Balances(this.#balances.filter(balance => !ids.includes(balance.id)));
  };

  // TODO: Add some more useful aggregator methods

  get each() {
    return [...this];
  }

  /**
   * Get an array of balances in this collection, sorted by chain sortIndex.
   *
   * @returns A sorted array of the balances in this collection.
   */
  get sorted() {
    return [...this].sort((a, b) => ((a.chain || a.evmNetwork)?.sortIndex ?? Number.MAX_SAFE_INTEGER) - ((b.chain || b.evmNetwork)?.sortIndex ?? Number.MAX_SAFE_INTEGER));
  }

  /**
   * Get the number of balances in this collection.
   *
   * @returns The number of balances in this collection.
   */
  get count() {
    return [...this].length;
  }

  /**
   * Get the summed value of balances in this collection.
   * TODO: Sum up token amounts AND fiat amounts
   *
   * @example
   * // Get the sum of all transferable balances in usd.
   * balances.sum.fiat('usd').transferable
   */
  get sum() {
    return new SumBalancesFormatter(this);
  }
}

/**
 * An individual balance.
 */
class Balance {
  //
  // Properties
  //

  /** The underlying data for this balance */
  #storage;
  #db = null;

  //
  // Methods
  //

  constructor(storage, hydrate) {
    this.#storage = storage;
    if (hydrate !== undefined) this.hydrate(hydrate);
  }
  toJSON = () => this.#storage;
  isSource = source => this.#storage.source === source;
  // // TODO: Fix this method, the types don't work with our plugin architecture.
  // // Specifically, the `BalanceJson` type is compiled down to `IBalance` in the following way:
  // //
  // //     toJSON: () => BalanceJson // works
  // //     isSource: (source: BalanceSource) => boolean // works
  // //     asSource: <P extends string>(source: P) => NarrowBalanceType<IBalance, P> | null // Doesn't work! IBalance should just be BalanceJson!
  // //
  // // `IBalance` won't match the type of `BalanceSource` after `PluginBalanceTypes` has been extended by balance plugins.
  // // As a result, typescript will think that the returned #storage is not a BalanceJson.
  // asSource = <P extends BalanceSource>(source: P): NarrowBalanceType<BalanceJson, P> | null => {
  //   if (this.#storage.source === source) return this.#storage as NarrowBalanceType<BalanceJson, P>
  //   return null
  // }

  hydrate = hydrate => {
    if (hydrate !== undefined) this.#db = hydrate;
  };
  #format = balance => new BalanceFormatter(typeof balance === "bigint" ? balance.toString() : balance, this.decimals || undefined, this.#db?.tokenRates && this.#db.tokenRates[this.tokenId]);

  //
  // Accessors
  //

  get id() {
    const {
      source,
      subSource,
      address,
      chainId,
      evmNetworkId,
      tokenId
    } = this.#storage;
    const locationId = chainId !== undefined ? chainId : evmNetworkId;
    return [source, address, locationId, tokenId, subSource].filter(Boolean).join("-");
  }
  get source() {
    return this.#storage.source;
  }
  get status() {
    return this.#storage.status;
  }
  get address() {
    return this.#storage.address;
  }
  get chainId() {
    return this.#storage.chainId;
  }
  get chain() {
    return this.#db?.chains && this.chainId && this.#db?.chains[this.chainId] || null;
  }
  get evmNetworkId() {
    return this.#storage.evmNetworkId;
  }
  get evmNetwork() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.#db?.evmNetworks && this.#db?.evmNetworks[this.evmNetworkId] || null;
  }
  get tokenId() {
    return this.#storage.tokenId;
  }
  get token() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.#db?.tokens && this.#db?.tokens[this.tokenId] || null;
  }
  get decimals() {
    return this.token?.decimals || null;
  }
  get rates() {
    return this.#db?.tokenRates && this.#db.tokenRates[this.tokenId] || null;
  }

  /**
   * The total balance of this token.
   * Includes the free and the reserved amount.
   * The balance will be reaped if this goes below the existential deposit.
   */
  get total() {
    const extra = includeInTotalExtraAmount(this.#storage.extra);
    return this.#format(this.free.planck + this.reserved.planck + extra);
  }
  /** The non-reserved balance of this token. Includes the frozen amount. Is included in the total. */
  get free() {
    return this.#format(typeof this.#storage.free === "string" ? BigInt(this.#storage.free) : Array.isArray(this.#storage.free) ? this.#storage.free.map(reserve => BigInt(reserve.amount)).reduce((a, b) => a + b, 0n) : BigInt(this.#storage.free?.amount || "0"));
  }
  /** The reserved balance of this token. Is included in the total. */
  get reserved() {
    return this.#format(typeof this.#storage.reserves === "string" ? BigInt(this.#storage.reserves) : Array.isArray(this.#storage.reserves) ? this.#storage.reserves.map(reserve => BigInt(reserve.amount)).reduce((a, b) => a + b, 0n) : BigInt(this.#storage.reserves?.amount || "0"));
  }
  get reserves() {
    return (Array.isArray(this.#storage.reserves) ? this.#storage.reserves : [this.#storage.reserves]).flatMap(reserve => {
      if (reserve === undefined) return [];
      if (typeof reserve === "string") return {
        label: "reserved",
        amount: this.#format(reserve)
      };
      return {
        ...reserve,
        amount: this.#format(reserve.amount)
      };
    });
  }
  /** The frozen balance of this token. Is included in the free amount. */
  get locked() {
    return this.#format(typeof this.#storage.locks === "string" ? BigInt(this.#storage.locks) : Array.isArray(this.#storage.locks) ? this.#storage.locks.map(lock => BigInt(lock.amount)).reduce((a, b) => BigMath.max(a, b), 0n) : BigInt(this.#storage.locks?.amount || "0"));
  }
  get locks() {
    return (Array.isArray(this.#storage.locks) ? this.#storage.locks : [this.#storage.locks]).flatMap(lock => {
      if (lock === undefined) return [];
      if (typeof lock === "string") return {
        label: "other",
        amount: this.#format(lock)
      };
      return {
        ...lock,
        amount: this.#format(lock.amount)
      };
    });
  }
  /** @deprecated Use balance.locked */
  get frozen() {
    return this.locked;
  }
  /** The transferable balance of this token. Is generally the free amount - the miscFrozen amount. */
  get transferable() {
    // if no locks exist, transferable is equal to the free amount
    if (!this.#storage.locks) return this.free;

    // find the largest lock (but ignore any locks which are marked as `includeInTransferable`)
    const excludeAmount = excludeFromTransferableAmount(this.#storage.locks);

    // subtract the lock from the free amount (but don't go below 0)
    return this.#format(BigMath.max(this.free.planck - excludeAmount, 0n));
  }
  /** The feePayable balance of this token. Is generally the free amount - the feeFrozen amount. */
  get feePayable() {
    // if no locks exist, feePayable is equal to the free amount
    if (!this.#storage.locks) return this.free;

    // find the largest lock which can't be used to pay tx fees
    const excludeAmount = excludeFromFeePayableLocks(this.#storage.locks).map(lock => BigInt(lock.amount)).reduce((max, lock) => BigMath.max(max, lock), 0n);

    // subtract the lock from the free amount (but don't go below 0)
    return this.#format(BigMath.max(this.free.planck - excludeAmount, 0n));
  }
}
class BalanceFormatter {
  #planck;
  #decimals;
  #fiatRatios;
  constructor(planck, decimals, fiatRatios) {
    this.#planck = typeof planck === "bigint" ? planck.toString() : planck ?? "0";
    this.#decimals = decimals || 0;
    this.#fiatRatios = fiatRatios || null;
  }
  toJSON = () => this.#planck;
  get planck() {
    return BigInt(this.#planck);
  }
  get tokens() {
    return planckToTokens(this.#planck, this.#decimals);
  }
  fiat(currency) {
    if (!this.#fiatRatios) return null;
    const ratio = this.#fiatRatios[currency];
    if (!ratio) return null;
    return parseFloat(this.tokens) * ratio;
  }
}
class PlanckSumBalancesFormatter {
  #balances;
  constructor(balances) {
    this.#balances = balances;
  }
  #sum = balanceField => {
    // a function to get a planck amount from a balance
    const planck = balance => balance[balanceField].planck ?? 0n;
    return this.#balances.filterMirrorTokens().each.reduce(
    // add the total amount to the planck amount of each balance
    (total, balance) => total + planck(balance),
    // start with a total of 0
    0n);
  };

  /**
   * The total balance of these tokens. Includes the free and the reserved amount.
   */
  get total() {
    return this.#sum("total");
  }
  /** The non-reserved balance of these tokens. Includes the frozen amount. Is included in the total. */
  get free() {
    return this.#sum("free");
  }
  /** The reserved balance of these tokens. Is included in the total. */
  get reserved() {
    return this.#sum("reserved");
  }
  /** The frozen balance of these tokens. Is included in the free amount. */
  get locked() {
    return this.#sum("locked");
  }
  /** @deprecated Use balances.locked */
  get frozen() {
    return this.locked;
  }
  /** The transferable balance of these tokens. Is generally the free amount - the miscFrozen amount. */
  get transferable() {
    return this.#sum("transferable");
  }
  /** The feePayable balance of these tokens. Is generally the free amount - the feeFrozen amount. */
  get feePayable() {
    return this.#sum("feePayable");
  }
}
class FiatSumBalancesFormatter {
  #balances;
  #currency;
  constructor(balances, currency) {
    this.#balances = balances;
    this.#currency = currency;
  }
  #sum = balanceField => {
    // a function to get a fiat amount from a balance
    const fiat = balance => balance[balanceField].fiat(this.#currency) ?? 0;
    return this.#balances.filterMirrorTokens().each.reduce(
    // add the total amount to the fiat amount of each balance
    (total, balance) => total + fiat(balance),
    // start with a total of 0
    0);
  };

  /**
   * The total balance of these tokens. Includes the free and the reserved amount.
   */
  get total() {
    return this.#sum("total");
  }
  /** The non-reserved balance of these tokens. Includes the frozen amount. Is included in the total. */
  get free() {
    return this.#sum("free");
  }
  /** The reserved balance of these tokens. Is included in the total. */
  get reserved() {
    return this.#sum("reserved");
  }
  /** The frozen balance of these tokens. Is included in the free amount. */
  get locked() {
    return this.#sum("locked");
  }
  /** @deprecated Use balances.locked */
  get frozen() {
    return this.locked;
  }
  /** The transferable balance of these tokens. Is generally the free amount - the miscFrozen amount. */
  get transferable() {
    return this.#sum("transferable");
  }
  /** The feePayable balance of these tokens. Is generally the free amount - the feeFrozen amount. */
  get feePayable() {
    return this.#sum("feePayable");
  }
}
class SumBalancesFormatter {
  #balances;
  constructor(balances) {
    this.#balances = balances;
  }
  get planck() {
    return new PlanckSumBalancesFormatter(this.#balances);
  }
  fiat(currency) {
    return new FiatSumBalancesFormatter(this.#balances, currency);
  }
}

export { Balance, BalanceFormatter, BalanceStatusLive, Balances, DefaultBalanceModule, FiatSumBalancesFormatter, PlanckSumBalancesFormatter, RpcStateQueryHelper, StorageHelper, SumBalancesFormatter, TalismanBalancesDatabase, balances, createSubscriptionId, createTypeRegistryCache, db, deleteSubscriptionId, deriveStatuses, excludeFromFeePayableLocks, excludeFromTransferableAmount, filterMirrorTokens, findChainMeta, getValidSubscriptionIds, includeInTotalExtraAmount };
